const handleize = (str) => {
  return str
    .toLowerCase()
    .trim()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const convertToObject = (arr) => {
  const result = {};
  
  arr.forEach(item => {
    const [key, value] = item.split(":");
    result[key] = value;
  });
  
  return result;
}

function parseInventoryData(dataStr) {
  const result = {};

  const handleSections = dataStr.split(";");

  for (const section of handleSections) {
    if (!section.includes("=")) continue;

    const [handle, variantsStr] = section.split("=");
    const variants = variantsStr.split(",");

    const variantObj = {};

    for (const entry of variants) {
      if (!entry.trim()) continue;

      const [variantId, qty] = entry.split(":");
      const quantity = qty && !isNaN(qty) ? Number(qty.trim()) : 0;

      variantObj[variantId.trim()] = quantity;
    }

    result[handle.trim()] = variantObj;
  }

  return result;
}

function mergeQuantitiesIntoProducts(products, productQuantities, patternTypesArr = []) {
  const patterns = convertToObject(patternTypesArr) || {};

  return products.map(product => {
    const handle = product.handle.trim();
    const quantities = productQuantities[handle] || {};
    const pattern = patterns[handle] || null

    const {
      description,
      content,
      ...cleanedProduct
    } = product;

    const variantsWithQty = cleanedProduct.variants.map(variant => ({
      ...variant,
      quantity: quantities[variant.id] ?? 0,
    }));

    return {
      ...cleanedProduct,
      variants: variantsWithQty,
      pattern
    };
  });
}

class ProductVariantPicker extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.renderRelatedProducts();

    this.colorWrapper.setAttribute("data-pattern", this.patternType);

    this.colorWrapper?.addEventListener("click", this.onButtonClick.bind(this), { signal });
    this.patternSelectors.forEach(input => {
      input?.addEventListener("change", this.onPatternSelect.bind(this), { signal });
    });

    this.selectedColor = this.selectedColorName;

    window.prefetchedProductsHTML = window.prefetchedProductsHTML || {};

    this.initFitModal();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  // ===================
  // GETTERS / SETTERS
  // ===================
  get colorSwatches() {
    return this.dataset.colorSwatches;
  }

  get styleCode() {
    return this.dataset.styleCode; 
  }

  get productId() {
    return Number(this.dataset.productId);
  }

  get colorWrapper() {
    return this.querySelector("[data-color-wrapper]");
  }

  get productHandle() {
    return this.dataset.productUrl.replace("/products/", "");
  }

  get patternType() {
    return this.dataset.patternType;
  }

  get patternWrapper() {
    return this.querySelector("[data-pattern-wrapper]");
  }

  get patternSelectors() {
    return this.querySelectorAll("[data-pattern-btn]");
  }

  set patternSelector(value) {
    this.patternSelectors.forEach(input => {
      input.checked = input.value == value;
    });
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  get selectedColorField() {
    return this.querySelector("[data-selected-color]");
  }

  set selectedColor(value) {
    this.selectedColorField.innerText = value || "";
  }

  get selectedColorName() {
    return this.querySelector(".pf-color-swatch.is-selected")?.dataset.colorName;
  }

  get swatchOrders() {
    const swatches = (this.dataset.swatchOrders || "").split("|");
    
    const solid = (swatches[0] || "")?.replace("solid:", "").split(",");
    const printed = (swatches[1] || "")?.replace("printed:", "").split(",");

    return {
      solid,
      printed
    };
  }

  // ===================
  // MAIN HANDLER
  // ===================
  async onButtonClick(e) {
    const button = e.target.closest("[data-color-btn]");
    if (!button) return;

    const handle = button.dataset.productHandle;
    if(handle == this.productHandle) return;

    this.updateSelectedSwatch(button);

    const colorWrapperCopy = this.colorWrapper.cloneNode(true);
    const patternWrapperCopy = this.patternWrapper.cloneNode(true);

    const productId = button.dataset.productId;

    if (window.prefetchedProductsHTML?.[handle]) {
      this.updateProductPage(window.prefetchedProductsHTML[handle], colorWrapperCopy, patternWrapperCopy, `${window.Shopify.routes.root}products/${handle}`, productId);
    } else {
      const url = `${window.Shopify.routes.root}products/${handle}`;
      try {
        const html = await this.fetchProductPage(url);
        this.updateProductPage(html, colorWrapperCopy, patternWrapperCopy, url, productId);
        
        window.prefetchedProductsHTML[handle] = html;
      } catch (err) {
        console.error("Error updating product page:", err);
      }
    }

    var load_event = new Event('load');
    window.dispatchEvent(load_event);
  }

  onPatternSelect(e) {
    const pattern = e.target.value;
    const selector = `.pf-color-swatch[data-pattern="${pattern}"]`;
    const button = this.colorWrapper.querySelector(selector);

    if(button) {
      this.colorWrapper.setAttribute("data-pattern", pattern);
      button.click();
    } else {
      this.patternSelector = pattern == "solid" ? "printed" : "solid";
    }
  }

  // ===================
  // HELPER FUNCTIONS
  // ===================

  updateSelectedSwatch(button) {
    document.querySelector(".pf-color-swatch.is-selected")?.classList.remove("is-selected");
    button.classList.add("is-selected");
  }

  async fetchProductPage(url) {
    const res = await fetch(url + "?section_id=" + this.sectionId);
    return res.text();
  }

  updateProductPage(html, colorWrapperCopy, patternWrapperCopy, url, productId) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    doc.querySelector("[data-color-wrapper]").replaceWith(colorWrapperCopy);

    doc.querySelector("[data-pattern-wrapper]").setAttribute("data-solid", patternWrapperCopy.dataset.solid);
    doc.querySelector("[data-pattern-wrapper]").setAttribute("data-printed", patternWrapperCopy.dataset.printed);

    const domSectionId = `#shopify-section-${this.sectionId}`

    document.querySelector(domSectionId).innerHTML = doc.querySelector(domSectionId).innerHTML;

    const junipBlock = document.querySelector(".junip-product-review");

    junipBlock.setAttribute("data-product-id", productId);
    junipBlock.setAttribute("id", `junipProductReviews${productId}`);

    document.title = doc.title;
    this.updateMetaTags(doc);

    this.reloadRechargeWidget(doc);

    window.history.pushState({}, "", url);

    this.loadAsyncScripts();
  }

  updateMetaTags(doc) {
    const newMetas = doc.head.querySelectorAll("meta[name], meta[property]");
    document.querySelectorAll("meta[name], meta[property]").forEach(m => m.remove());
    newMetas.forEach(m => document.head.appendChild(m));
  }

  reloadRechargeWidget(doc) {
    const appBlock = document.querySelector(".recharge-subscription-widget");
    if(!appBlock) return;
    
    const appBlockChildren = Array.from(doc.querySelectorAll(".recharge-subscription-widget > *"));
    appBlock.innerHTML = "";

    appBlockChildren.forEach(child => {
      if (child.tagName === "SCRIPT") {
        const newScript = document.createElement("script");
        Array.from(child.attributes).forEach(attr => newScript.setAttribute(attr.name, attr.value));
        if (child.textContent) newScript.textContent = child.textContent;
        appBlock.appendChild(newScript);
      } else {
        appBlock.appendChild(child.cloneNode(true));
      }
    });
  }

  loadAsyncScripts() {
    const urls = [
      "https://static.rechargecdn.com/assets/js/widget.min.js?shop=tradie-au.myshopify.com",
      "https://widgets.juniphq.com/v1/junip_shopify.js?shop=tradie-au.myshopify.com",
      "https://chimpstatic.com/mcjs-connected/js/users/b4dc8c17a306fc7ea974d3f43/0c97f6952801355aae1c2be26.js?shop=tradie-au.myshopify.com"
    ];

    urls.forEach(src => {
      if (!document.querySelector(`script[src="${src}"]`)) {
        const s = document.createElement("script");
        s.type = "text/javascript";
        s.async = true;
        s.src = src;
        document.head.appendChild(s);
      }
    });

    const widgetContainer = document.querySelector(".recharge-subscription-widget");
    if (widgetContainer && !widgetContainer.querySelector("script[type='module']")) {
      const moduleScript = document.createElement("script");
      moduleScript.type = "module";
      moduleScript.src = "https://static.rechargecdn.com/assets/subscription-widget/subscription-widget.js";
      widgetContainer.appendChild(moduleScript);
    }
  }

  // ===================
  // RELATED PRODUCTS
  // ===================
  async renderRelatedProducts(styleCode = null) {
    const code = styleCode || this.styleCode;
    if(!code) return;

    const productsData = await this.getRelatedProducts(code);
    let products = productsData.products;

    const patterns = ["solid", "printed"];
    this.productsWithQuantities = [];

    patterns.forEach(pattern => {
      const patternProducts =  products.filter(p => p.pattern == pattern);
      const swatchOrders = this.swatchOrders?.[pattern] || [];

      swatchOrders.forEach(color => {
        const ind = patternProducts.findIndex(p => p.variants?.[0]?.option1?.trim().toLowerCase() == color.trim().toLowerCase());

        if(ind != -1) {
          this.productsWithQuantities.push(patternProducts[ind]);
          patternProducts.splice(ind, 1);
        }
      });

      if(patternProducts.length > 0) this.productsWithQuantities.push(...patternProducts);
    });

    if(products.length != this.productsWithQuantities.length) {
      const restOfProducts = products.filter(p => !patterns.includes(p.pattern));
      this.productsWithQuantities.push(...restOfProducts);
    }

    products = this.productsWithQuantities;

    this.patternTypes = productsData.patterns;
    
    this.patternWrapper.setAttribute("data-solid", productsData.solidPatterns);
    this.patternWrapper.setAttribute("data-printed", productsData.printedPatterns);
    this.colorWrapper.setAttribute("data-patterns", productsData.totalPatterns);
    this.colorWrapper.setAttribute("data-products", products.length);

    if(!window.relatedProductsLoaded) {
      this.colorWrapper.querySelectorAll("[data-color-btn]")?.forEach(btn => btn.remove());

      products.sort((a, b) => {
        if (a.id === this.productId) return -1;
        if (b.id === this.productId) return 1;
        return 0;
      });

      products.forEach((product) => {
        if (!(product.variants?.length > 0)) return;
        const color = product.variants[0].option1;
        if (!color) return;

        const buttonEl = this.getColorButton(color, product.handle, product.id === this.productId, product.id, product.featured_image);
        if (buttonEl) this.colorWrapper.insertAdjacentHTML("beforeend", buttonEl);
      });
    }

    window.relatedProductsLoaded = true;
    
    await Promise.all(products.map(async (product) => {
      if (!window.prefetchedProductsHTML[product.handle]) {
        const url = `${window.Shopify.routes.root}products/${product.handle}`;
        const res = await fetch(url);

        window.prefetchedProductsHTML[product.handle] = await res.text();
      }
    }));
  }

  async getRelatedProducts(styleCode) {
    try {
      const productsObj = {
          products: [],
          patterns: {},
          totalPatterns: 0,
          solidPatterns: 0,
          printedPatterns: 0
        };

      const url = `/collections/all?filter.p.m.custom.ap21_style_code=${styleCode}&section_id=main-collection-grid`;
      const response = await fetch(url);
      const html = await response.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      const jsonElement = doc.querySelector(".pf-products-json");

      if (!jsonElement) {
        console.warn("No .pf-products-json element found in response");

        return productsObj;
      }

      try {
        productsObj.products = JSON.parse(jsonElement.dataset.products);

        const filteredProducts = productsObj.products;

        const productQuantities = parseInventoryData(jsonElement.dataset.productQuantities);

        const patternTypesArr = (jsonElement.dataset.patternTypes || "").split(",");
        productsObj.products = mergeQuantitiesIntoProducts(filteredProducts, productQuantities, patternTypesArr);

        patternTypesArr.forEach(patternStr => {
          const patternArr = (patternStr || "").split(":");
          
          if(!patternArr[0]) return;

          if(patternArr[1] == "solid") {
            productsObj.solidPatterns++;
            productsObj.totalPatterns++;
          } else if(patternArr[1] == "printed") {
            productsObj.printedPatterns++;
            productsObj.totalPatterns++;
          }
          
          productsObj.patterns[patternArr[0]] = (patternArr[1] || "");
        })
        
        return productsObj;
      } catch (err) {
        console.error("Error parsing .pf-products-json JSON", err);
        return productsObj;
      }
    } catch (error) {
      console.error("Error loading related products:", error);
      return productsObj;
    }
  }

  // ===================
  // BUTTON BUILDER
  // ===================
  getColorButton(color, productHandle, isActive, productId, featuredImage) {
    const pattern = this.patternTypes[productHandle];
    // if(!pattern && !isActive) return null;
    
    const currentColor = handleize(color);
    const swatchLines = this.colorSwatches.split(/\r?\n/);

    let colorValue = null;
    swatchLines.forEach(line => {
      const [name, value] = line.split(":").map(s => s.trim());
      if (!name || !value) return;
      if (currentColor.includes(handleize(name))) {
        colorValue = value;
      }
    });

    let style = /\.(jpg|png)$/i.test(colorValue)
      ? `background-image: url('https://cdn.shopify.com/s/files/1/0645/1996/2668/files/${colorValue}')`
      : `background: linear-gradient(to right, ${colorValue}, ${colorValue})`;

    if (!colorValue) style = `background-image: url('${featuredImage}')`;

    return `
      <button
        class="pf-color-swatch ${isActive ? 'is-selected' : ''}"
        data-color-name="${color}"
        data-product-handle="${productHandle}"
        data-product-id="${productId}"
        data-pattern="${pattern}"
        data-color-btn
      >
        <span class="pf-color-icon" style="${style};"></span>
        <p class="pf-color-tooltip"><span>${color}</span></p>
      </button>
    `;
  }


  // ===================
  // Extra Script Load
  // ===================
  initFitModal() {
    var modal = document.getElementById('fitModal');
    var overlay = document.getElementById('modalOverlay');
    var btn = document.getElementById('openFitModal');
    var span = document.getElementsByClassName('close-fit-modal')[0];

    span.onclick = function () {
      closeModal();
    };

    overlay.onclick = function () {
      closeModal();
    };

    function closeModal() {
      modal.classList.remove('open');
      setTimeout(function () {
        modal.style.display = 'none';
        overlay.classList.remove('open');
      }, 300);
    }
  }
}

if(!customElements.get("product-variant-picker")) {
  customElements.define("product-variant-picker", ProductVariantPicker);
}
